/**
 * Offline retry queue for booth check-ins (Conversion Engine Stage 2a).
 *
 * Mirrors `redemptionQueue.ts` exactly in shape and bounds, but is a SEPARATE
 * sibling (own storage key) so the live /redeem retry path is untouched. We keep
 * them separate rather than refactoring the production redemption queue under a
 * generic — same pattern, zero blast radius on redemptions.
 *
 * Why offline retry is simple here: `process_voucher_checkin` is idempotent on
 * the client key, so a duplicate submission on reconnect just returns success
 * (value_escalated=false). There is no "already redeemed" special case — a
 * replay is a success, not a business failure.
 *
 * The queued payload carries ONLY FACTS — {event_id, voucher_code, attended,
 * brought_friend, friend_joined, client_idempotency_key} — NEVER a dollar
 * amount. The server computes value on sync, so a blind offline submit is safe.
 *
 * Audit-imposed bounds (same as the redemption queue):
 *   - 24h TTL, max 5 retries per entry, queue cap 25.
 *   - Stable local id; status 'pending' | 'retrying' | 'failed'.
 *   - Storage: a single localStorage key holding the JSON array; read-modify-
 *     write per op (single-tab booth device; not cross-tab safe).
 */

import { submitCheckin, type CheckinInput } from "./checkin.service";

const STORAGE_KEY = "checkin_retry_queue_v1";
const TTL_MS = 24 * 60 * 60 * 1000;
const MAX_ATTEMPTS = 5;
const QUEUE_CAP = 25;

export type CheckinQueueStatus = "pending" | "retrying" | "failed";

/** Snapshot for rendering the banner + a post-sync success line. */
export interface QueuedCheckinDisplayContext {
  /** Member label to show in the banner (name or email). */
  memberLabel: string;
}

export interface QueuedCheckin {
  id: string;
  payload: CheckinInput;
  enqueuedAt: number;
  lastAttemptAt: number | null;
  attemptCount: number;
  status: CheckinQueueStatus;
  lastError?: string;
  /** Display label for the banner — voucher code uppercase. */
  voucherCode: string;
  displayContext?: QueuedCheckinDisplayContext;
}

export interface CheckinProcessOutcome {
  processed: number;
  succeeded: number;
  businessFailures: number;
  networkFailures: number;
  failed: number; // attempts >= MAX_ATTEMPTS
  remaining: number;
}

function readRaw(): QueuedCheckin[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed as QueuedCheckin[];
  } catch {
    return [];
  }
}

function writeRaw(entries: QueuedCheckin[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (err) {
    console.warn("Failed to persist check-in queue:", err);
  }
}

function pruneExpired(entries: QueuedCheckin[]): QueuedCheckin[] {
  const cutoff = Date.now() - TTL_MS;
  return entries.filter((e) => e.enqueuedAt >= cutoff);
}

export function getQueue(): QueuedCheckin[] {
  const pruned = pruneExpired(readRaw());
  writeRaw(pruned);
  return pruned;
}

export function getQueueCount(): number {
  return getQueue().length;
}

/**
 * Add a check-in to the queue. Returns the entry, or null if the queue is at
 * cap. Forces source='booth_offline_sync' so the ledger records the origin.
 */
export function enqueue(
  payload: CheckinInput,
  displayContext?: QueuedCheckinDisplayContext,
): QueuedCheckin | null {
  const entries = pruneExpired(readRaw());
  if (entries.length >= QUEUE_CAP) {
    return null;
  }
  const entry: QueuedCheckin = {
    id:
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    payload: { ...payload, source: "booth_offline_sync" },
    enqueuedAt: Date.now(),
    lastAttemptAt: null,
    attemptCount: 0,
    status: "pending",
    voucherCode: (payload.voucher_code || "").toUpperCase(),
    displayContext,
  };
  entries.push(entry);
  writeRaw(entries);
  return entry;
}

export function removeEntry(id: string): void {
  const entries = readRaw().filter((e) => e.id !== id);
  writeRaw(entries);
}

export function clearAll(): void {
  writeRaw([]);
}

export interface CheckinProcessHooks {
  onSuccess?: (entry: QueuedCheckin) => void;
  onBusinessFailure?: (entry: QueuedCheckin, message: string) => void;
  onMaxAttempts?: (entry: QueuedCheckin) => void;
}

/**
 * Process the queue once, attempting each pending entry sequentially. Sequential
 * (not parallel) to avoid a thundering herd on reconnect and to keep error
 * attribution simple. The server's idempotency makes re-submitting safe.
 */
export async function processQueue(
  hooks: CheckinProcessHooks = {},
): Promise<CheckinProcessOutcome> {
  const outcome: CheckinProcessOutcome = {
    processed: 0,
    succeeded: 0,
    businessFailures: 0,
    networkFailures: 0,
    failed: 0,
    remaining: 0,
  };

  let entries = pruneExpired(readRaw());
  const pending = entries.filter(
    (e) => e.status === "pending" || e.status === "retrying",
  );

  for (const entry of pending) {
    outcome.processed++;
    entries = readRaw().map((e) =>
      e.id === entry.id
        ? { ...e, status: "retrying" as CheckinQueueStatus }
        : e,
    );
    writeRaw(entries);

    let result;
    try {
      result = await submitCheckin(entry.payload);
    } catch (err) {
      result = {
        success: false,
        failureKind: "network" as const,
        message: err instanceof Error ? err.message : "Unknown error",
      };
    }

    const now = Date.now();

    if (result.success) {
      outcome.succeeded++;
      hooks.onSuccess?.(entry);
      const after = readRaw().filter((e) => e.id !== entry.id);
      writeRaw(after);
      continue;
    }

    if (result.failureKind === "business") {
      outcome.businessFailures++;
      hooks.onBusinessFailure?.(entry, result.message || "Check-in rejected.");
      // Won't succeed on retry — mark failed, keep for inspection.
      const after = readRaw().map((e) =>
        e.id === entry.id
          ? {
              ...e,
              status: "failed" as CheckinQueueStatus,
              lastAttemptAt: now,
              lastError: result.message,
            }
          : e,
      );
      writeRaw(after);
      continue;
    }

    // Network failure — bump attemptCount; retry-pending or fail if exhausted.
    outcome.networkFailures++;
    const newAttempt = entry.attemptCount + 1;
    if (newAttempt >= MAX_ATTEMPTS) {
      outcome.failed++;
      hooks.onMaxAttempts?.(entry);
      const after = readRaw().map((e) =>
        e.id === entry.id
          ? {
              ...e,
              status: "failed" as CheckinQueueStatus,
              attemptCount: newAttempt,
              lastAttemptAt: now,
              lastError: result.message,
            }
          : e,
      );
      writeRaw(after);
    } else {
      const after = readRaw().map((e) =>
        e.id === entry.id
          ? {
              ...e,
              status: "pending" as CheckinQueueStatus,
              attemptCount: newAttempt,
              lastAttemptAt: now,
              lastError: result.message,
            }
          : e,
      );
      writeRaw(after);
    }
  }

  outcome.remaining = readRaw().length;
  return outcome;
}

/**
 * Retry a single entry — including a `failed` one. `processQueue` only touches
 * pending|retrying rows, so a row that exhausted its attempts (or hit a business
 * failure) is otherwise skipped forever. We reset it to pending with a fresh
 * attempt budget, then run the queue. Returns the process outcome, or null if the
 * id is gone (already synced/dismissed). The check-in RPC is idempotent on the
 * client key, so re-submitting a row that actually went through is safe.
 */
export async function retryEntry(
  id: string,
  hooks: CheckinProcessHooks = {},
): Promise<CheckinProcessOutcome | null> {
  const entries = readRaw();
  if (!entries.some((e) => e.id === id)) {
    return null;
  }
  writeRaw(
    entries.map((e) =>
      e.id === id
        ? {
            ...e,
            status: "pending" as CheckinQueueStatus,
            attemptCount: 0,
            lastError: undefined,
            lastAttemptAt: null,
          }
        : e,
    ),
  );
  return processQueue(hooks);
}

export const CHECKIN_QUEUE_BOUNDS = {
  TTL_MS,
  MAX_ATTEMPTS,
  QUEUE_CAP,
};
